import React from 'react';
import { DiscountValueType } from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { PricedItem, discountedPrice, priceExtremes } from '../../utils/discountPricing';
import { formatTenge } from '../../utils/money';
import { fill } from '../../utils/template';
import { FormCard } from '../form/FormShell';
import { Field } from '../form/Field';
import { AMOUNT_MIN, PERCENT_MAX, PERCENT_MIN, parseWhole } from './discountDraft';
import { Segmented } from './Segmented';

interface DiscountValueCardProps {
  valueType: DiscountValueType;
  percent: string;
  amount: string;
  /** Позиции цели; null — цены неизвестны, превью не строим. */
  targetItems: PricedItem[] | null;
  /** Цель выбрана: пустое превью тогда значит «в разделе нет позиций». */
  targetChosen: boolean;
  error?: string;
  disabled: boolean;
  onValueTypeChange: (valueType: DiscountValueType) => void;
  onPercentChange: (value: string) => void;
  onAmountChange: (value: string) => void;
}

/** Размер годится для превью: процент 1–100, тенге от 1. */
function usableValue(valueType: DiscountValueType, value: number | null): number | null {
  if (value == null) return null;
  if (valueType === 'percent') return value >= PERCENT_MIN && value <= PERCENT_MAX ? value : null;

  return value >= AMOUNT_MIN ? value : null;
}

interface PricePreviewProps {
  items: PricedItem[] | null;
  targetChosen: boolean;
  valueType: DiscountValueType;
  value: number | null;
}

/**
 * «Было → стало» на самой дешёвой и самой дорогой позиции цели: по двум
 * краям видно, что скидка делает со всем разделом. Считается по тем же
 * правилам, что на сервере, — `discountedPrice`.
 */
function PricePreview({ items, targetChosen, valueType, value }: PricePreviewProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  if (items == null) return null;

  const extremes = priceExtremes(items);
  const rows = !extremes
    ? []
    : extremes.cheapest.id === extremes.priciest.id
      ? [{ label: strings.previewSingle, item: extremes.cheapest }]
      : [
          { label: strings.previewCheapest, item: extremes.cheapest },
          { label: strings.previewPriciest, item: extremes.priciest },
        ];

  return (
    <div className="qp-preview">
      <span className="qp-field-label" id="discount-preview-label">
        {strings.preview}
      </span>
      {rows.length === 0 ? (
        <p className="qp-preview-empty">{targetChosen ? strings.treeEmpty : strings.previewEmpty}</p>
      ) : (
        <ul className="qp-preview-rows" aria-labelledby="discount-preview-label">
          {rows.map(({ label, item }) => {
            const now = value != null ? discountedPrice(item.price, valueType, value) : null;
            // Тенге больше цены — цена упрётся в ноль; сервер такую скидку не примет.
            const broken = valueType === 'amount' && value != null && value > item.price;

            return (
              <li key={label} className={`qp-preview-row${broken ? ' qp-preview-row--bad' : ''}`}>
                <span className="qp-preview-k">{label}</span>
                <span className="qp-preview-name" title={item.name}>
                  {item.name}
                </span>
                <span className="qp-preview-prices">
                  {now == null ? (
                    <span>{formatTenge(item.price)}</span>
                  ) : (
                    <>
                      <del className="qp-preview-was">{formatTenge(item.price)}</del>
                      <span className="qp-preview-arrow" aria-hidden>
                        →
                      </span>
                      <ins className="qp-preview-now">{formatTenge(now)}</ins>
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Размер групповой скидки: число и единица рядом, превью — под ними. */
export function DiscountValueCard({
  valueType,
  percent,
  amount,
  targetItems,
  targetChosen,
  error,
  disabled,
  onValueTypeChange,
  onPercentChange,
  onAmountChange,
}: DiscountValueCardProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  const isPercent = valueType === 'percent';
  const raw = isPercent ? percent : amount;
  const extremes = targetItems ? priceExtremes(targetItems) : null;

  const hint = isPercent
    ? strings.valuePercentHint
    : extremes
      ? fill(strings.valueAmountHintMax, { price: formatTenge(extremes.cheapest.price) })
      : strings.valueAmountHint;

  return (
    <FormCard title={strings.cardValue} hint={strings.cardValueHint}>
      <Field label={strings.value} required htmlFor="discount-value" error={error} hint={hint}>
        <div className="qp-value-row">
          <input
            id="discount-value"
            type="number"
            inputMode="numeric"
            min={isPercent ? PERCENT_MIN : AMOUNT_MIN}
            max={isPercent ? PERCENT_MAX : undefined}
            step={1}
            value={raw}
            onChange={(event) =>
              isPercent ? onPercentChange(event.target.value) : onAmountChange(event.target.value)
            }
            className="qp-control qp-control--num"
            aria-invalid={!!error}
            disabled={disabled}
          />
          <Segmented
            unit
            label={strings.valueUnit}
            value={valueType}
            options={[
              { value: 'percent', label: '%' },
              { value: 'amount', label: '₸' },
            ]}
            onChange={onValueTypeChange}
            disabled={disabled}
          />
        </div>
      </Field>

      <PricePreview
        items={targetItems}
        targetChosen={targetChosen}
        valueType={valueType}
        value={usableValue(valueType, parseWhole(raw))}
      />
    </FormCard>
  );
}
